import React, { useEffect, useState } from "react";

import {
  Badge,
  Layout,
  SettingToggle,
  TextField,
} from "@shopify/polaris";

import { useAdmin } from "context/admin";

export default function NotificationsSettings({ shop_config }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    order_notification_emails: shop_config.order_notification_emails || "",
    notify_on_order_create: shop_config.notify_on_order_create || false,
    notify_on_order_paid: shop_config.notify_on_order_paid || false,
    notify_on_unpaid_before_window:
      shop_config.notify_on_unpaid_before_window || false,
  });

  const admin = useAdmin();

  // Sync local form state with updated shop_config prop
  useEffect(() => {
    setForm({
      order_notification_emails: shop_config.order_notification_emails || "",
      notify_on_order_create: shop_config.notify_on_order_create || false,
      notify_on_order_paid: shop_config.notify_on_order_paid || false,
      notify_on_unpaid_before_window:
        shop_config.notify_on_unpaid_before_window || false,
    });
  }, [shop_config]);

  const handleUpdate = (key, value) => {
    setSaving(key);
    admin
      .updateShopConfig({ [key]: value })
      .then((shopConfig) => {
        setForm({ ...form, [key]: value });
      })
      .finally(() => setSaving(false));
  };

  const hasRecipients =
    typeof form.order_notification_emails === "string" &&
    form.order_notification_emails.trim().length > 0;

  return (
    <Layout.AnnotatedSection
      title="Notifications"
      description={
        <p>
          Send order event emails to store staff. Enter one or more recipient
          addresses and enable the events you want to be notified about.
        </p>
      }
    >
      <TextField
        label="Email notifications to"
        value={form.order_notification_emails}
        onChange={(val) =>
          setForm({ ...form, order_notification_emails: val })
        }
        onBlur={(event) =>
          handleUpdate("order_notification_emails", event.target.value)
        }
        helpText="Comma-separated. Leave blank to disable all notifications."
      />

      {hasRecipients ? (
        <>
          <div style={{ height: "1rem" }} />

          <SettingToggle
            action={{
              content: form.notify_on_order_create ? "Disable" : "Enable",
              onAction: () =>
                handleUpdate(
                  "notify_on_order_create",
                  !form.notify_on_order_create
                ),
              loading: saving === "notify_on_order_create",
              destructive: form.notify_on_order_create,
            }}
            enabled={form.notify_on_order_create ? true : false}
          >
            Order placed notifications are{" "}
            <Badge
              status={form.notify_on_order_create ? "success" : "default"}
            >
              {form.notify_on_order_create ? "Enabled" : "Disabled"}
            </Badge>
            .
          </SettingToggle>

          <div style={{ height: "1rem" }} />

          <SettingToggle
            action={{
              content: form.notify_on_order_paid ? "Disable" : "Enable",
              onAction: () =>
                handleUpdate(
                  "notify_on_order_paid",
                  !form.notify_on_order_paid
                ),
              loading: saving === "notify_on_order_paid",
              destructive: form.notify_on_order_paid,
            }}
            enabled={form.notify_on_order_paid ? true : false}
          >
            Payment received notifications are{" "}
            <Badge
              status={form.notify_on_order_paid ? "success" : "default"}
            >
              {form.notify_on_order_paid ? "Enabled" : "Disabled"}
            </Badge>
            .
          </SettingToggle>

          <div style={{ height: "1rem" }} />

          <SettingToggle
            action={{
              content: form.notify_on_unpaid_before_window
                ? "Disable"
                : "Enable",
              onAction: () =>
                handleUpdate(
                  "notify_on_unpaid_before_window",
                  !form.notify_on_unpaid_before_window
                ),
              loading: saving === "notify_on_unpaid_before_window",
              destructive: form.notify_on_unpaid_before_window,
            }}
            enabled={form.notify_on_unpaid_before_window ? true : false}
          >
            Unpaid before delivery/pickup window notifications are{" "}
            <Badge
              status={
                form.notify_on_unpaid_before_window ? "success" : "default"
              }
            >
              {form.notify_on_unpaid_before_window ? "Enabled" : "Disabled"}
            </Badge>
            .
          </SettingToggle>
        </>
      ) : null}
    </Layout.AnnotatedSection>
  );
}
